/**
 * Front desk — Walk-in inquiry (H-15) + Call log.
 *
 * Walk-in inquiry: feeds the admissions pipeline with source=walk_in.
 * Call log: real, backed by migration 030_call_log.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Form, Input, Modal, Radio, Segmented, Select, Skeleton, Space, Table, Tag,
  Typography, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type {
  CallLogRow, CreateCallLogRequest, CreateInquiryRequest, InquirySource,
} from '@velora/contracts';
import { ApiError, productApi } from '../../api';

/* ---------------- Walk-in inquiry ---------------- */

interface WalkinForm {
  parent_name: string;
  phone: string;
  email?: string;
  child_name?: string;
  grade_interest?: string;
  notes?: string;
}

export function WalkinInquiryPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm<WalkinForm>();
  const m = useMutation({
    mutationFn: (input: CreateInquiryRequest) => productApi.createInquiry(institution, input),
    onSuccess: () => {
      form.resetFields();
      void message.success('Inquiry logged');
      navigate(`/i/${institution}/admin/admissions/inquiries`);
    },
  });
  const err = m.error instanceof ApiError
    ? ((m.error.body as { message?: string } | null)?.message ?? null)
    : m.error instanceof Error ? m.error.message : null;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Walk-in inquiry</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          H-15 · adds to the admissions pipeline with source <b>walk_in</b> — admin picks it up from Admissions › Inquiries.
        </Typography.Text>
      </div>

      <Card size="small">
        <Form
          form={form} layout="vertical"
          onFinish={(v) => m.mutate({
            parent_name: v.parent_name.trim(),
            phone: v.phone.trim(),
            email: v.email?.trim() || undefined,
            child_name: v.child_name?.trim() || undefined,
            grade_interest: v.grade_interest?.trim() || undefined,
            notes: v.notes?.trim() || undefined,
            source: 'walk_in' satisfies InquirySource,
          })}
        >
          <Form.Item name="parent_name" label="Parent name" rules={[{ required: true, min: 1, max: 160 }]}>
            <Input placeholder="Full name" autoFocus />
          </Form.Item>
          <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
            <Input placeholder="+91 ..." style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
          </Form.Item>
          <Form.Item name="email" label="Email (optional)"><Input placeholder="—" /></Form.Item>
          <Form.Item name="child_name" label="Child name (optional)"><Input placeholder="—" /></Form.Item>
          <Form.Item name="grade_interest" label="Grade of interest (optional)"><Input placeholder="e.g. Grade 3" /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={3} placeholder="What they came in to ask about" /></Form.Item>
          {err && <Alert type="error" showIcon message={err} style={{ marginBottom: 12 }} />}
          <Button type="primary" htmlType="submit" loading={m.isPending} block>Log inquiry</Button>
        </Form>
      </Card>
    </Space>
  );
}

/* ---------------- Call log ---------------- */

const DIRECTION_TONE: Record<'inbound' | 'outbound', string> = { inbound: 'blue', outbound: 'purple' };

export function CallLogPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [filter, setFilter] = useState<'all' | 'pending'>('all');
  const [addOpen, setAddOpen] = useState(false);
  const q = useQuery({
    queryKey: ['calls', institution, filter],
    queryFn: () => productApi.listCalls(institution, filter === 'pending' ? { pending: true } : {}),
    enabled: !!institution,
  });
  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>Call log</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {q.data.total} logged · {q.data.pending_outcome} awaiting outcome
          </Typography.Text>
        </div>
        <Segmented
          size="small"
          value={filter}
          onChange={(v) => setFilter(v as 'all' | 'pending')}
          options={[{ label: 'All', value: 'all' }, { label: 'Pending', value: 'pending' }]}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Log call</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        <StatCard title="Inbound" value={q.data.by_direction.inbound} />
        <StatCard title="Outbound" value={q.data.by_direction.outbound} />
        <StatCard title="Pending outcome" value={q.data.pending_outcome} tone={q.data.pending_outcome > 0 ? 'warn' : 'ok'} />
      </div>

      <Card size="small">
        {q.data.rows.length === 0 ? (
          <Empty description="No calls logged in this view." style={{ padding: 20 }} />
        ) : (
          <Table<CallLogRow>
            size="small" pagination={{ pageSize: 20 }} rowKey="id"
            dataSource={q.data.rows}
            columns={[
              { title: 'When', dataIndex: 'at', key: 'at', width: 160,
                render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>
                  {new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span> },
              { title: 'Dir', dataIndex: 'direction', key: 'd', width: 90,
                render: (v: 'inbound' | 'outbound') => <Tag color={DIRECTION_TONE[v]}>{v}</Tag> },
              { title: 'Caller', key: 'c',
                render: (_, r) => <>
                  <b>{r.caller_name}</b>
                  <div style={{ fontSize: 11, color: '#8a94a6', fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{r.caller_phone}</div>
                </> },
              { title: 'Ward', key: 'w',
                render: (_, r) => r.student_name
                  ? <>{r.student_name} <span style={{ fontSize: 11, color: '#8a94a6' }}>· {r.admission_number}</span></>
                  : <Typography.Text type="secondary">—</Typography.Text> },
              { title: 'Purpose', dataIndex: 'purpose', key: 'p' },
              { title: 'Outcome', key: 'o',
                render: (_, r) => r.outcome
                  ? <span style={{ fontSize: 12 }}>{r.outcome}</span>
                  : <RecordOutcomeButton call={r} /> },
            ]}
          />
        )}
      </Card>

      <NewCallModal open={addOpen} institution={institution} onClose={() => setAddOpen(false)} />
    </Space>
  );
}

function StatCard({ title, value, tone }: { title: string; value: number; tone?: 'ok' | 'warn' }) {
  const color = tone === 'warn' && value > 0 ? '#a24040' : tone === 'ok' ? '#1c6442' : '#14213d';
  return (
    <Card size="small">
      <div style={{ fontSize: 11, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
    </Card>
  );
}

function RecordOutcomeButton({ call }: { call: CallLogRow }) {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState('');
  const m = useMutation({
    mutationFn: () => productApi.updateCallOutcome(institution, call.id, { outcome: outcome.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['calls', institution] });
      void message.success('Recorded');
      setOpen(false); setOutcome('');
    },
  });
  return (
    <>
      <Button size="small" onClick={() => setOpen(true)}>Add outcome</Button>
      <Modal open={open} title="Record outcome" onCancel={() => setOpen(false)}
        okText="Save" confirmLoading={m.isPending} onOk={() => outcome.trim().length > 0 && m.mutate()}>
        <Input.TextArea rows={4} value={outcome} onChange={(e) => setOutcome(e.target.value)}
          placeholder="What was resolved / promised / referred to?" />
      </Modal>
    </>
  );
}

interface CallForm {
  direction: 'inbound' | 'outbound';
  caller_name: string;
  caller_phone: string;
  purpose: string;
  admission_number?: string;
  handled_by?: string;
  outcome?: string;
}

function NewCallModal({
  open, institution, onClose,
}: { open: boolean; institution: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [form] = Form.useForm<CallForm>();
  const m = useMutation({
    mutationFn: (input: CreateCallLogRequest) => productApi.createCall(institution, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['calls', institution] });
      form.resetFields();
      void message.success('Logged');
      onClose();
    },
  });
  const err = m.error instanceof ApiError
    ? ((m.error.body as { message?: string } | null)?.message ?? null)
    : m.error instanceof Error ? m.error.message : null;
  return (
    <Modal open={open} title="Log a call"
      onCancel={() => { m.reset(); onClose(); }}
      okText="Log"
      confirmLoading={m.isPending}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Form form={form} layout="vertical"
        initialValues={{ direction: 'inbound' as 'inbound' | 'outbound' }}
        onFinish={(v) => m.mutate({
          direction: v.direction,
          caller_name: v.caller_name.trim(),
          caller_phone: v.caller_phone.trim(),
          purpose: v.purpose.trim(),
          admission_number: v.admission_number?.trim() || undefined,
          handled_by: v.handled_by?.trim() || undefined,
          outcome: v.outcome?.trim() || undefined,
        })}
      >
        <Form.Item name="direction" label="Direction" rules={[{ required: true }]}>
          <Radio.Group>
            <Radio.Button value="inbound">Inbound</Radio.Button>
            <Radio.Button value="outbound">Outbound</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="caller_name" label="Caller name" rules={[{ required: true, min: 1, max: 160 }]}>
          <Input autoFocus />
        </Form.Item>
        <Form.Item name="caller_phone" label="Phone" rules={[{ required: true }]}>
          <Input placeholder="+91 ..." style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
        </Form.Item>
        <Form.Item name="purpose" label="Purpose" rules={[{ required: true, min: 1, max: 300 }]}>
          <Input placeholder="e.g. Asking about admissions" />
        </Form.Item>
        <Form.Item name="admission_number" label="Ward admission number (optional)">
          <Input placeholder="Link to a student on file" />
        </Form.Item>
        <Form.Item name="handled_by" label="Handled by (optional)"><Input /></Form.Item>
        <Form.Item name="outcome" label="Outcome (optional — leave empty to fill later)">
          <Input.TextArea rows={2} />
        </Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}
